import {
  getMentionTextParts,
  isOnlyEmojis,
  replaceMentionPlaceholderWithNames,
  useTranslatedText,
} from '@gripp/shared-logic';
// import { useNavigation } from 'expo-router';
import { router } from 'expo-router';
import {
  ComponentType,
  ReactNode,
  createElement,
  useCallback,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Linking,
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
} from 'react-native';
import Hyperlink from 'react-native-hyperlink';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { getImageThumbnailWidth } from '../media/getMediaThumbnailWidth';
import { renderFormattedMessageText } from './formattedMessageText';
import { MessageMedia } from './messageMedia';
import { messageStyles } from './styles';
import { MessageComponent, MessageComponentProps } from './types';

export const UserMessage: MessageComponent = (props: MessageComponentProps) => {
  const { t } = useTranslation();
  const { message, imageDimensions } = props;
  const [mediaWidth, setMediaWidth] = useState<number>();
  const { originalText, translatedText, needsTranslation } = useTranslatedText(
    'text',
    message
  );
  const [text, setText] = useState(
    needsTranslation ? translatedText : originalText
  );

  // Shared by Hyperlink (bare URLs it detects in the text) and by the
  // `[label](url)` links the composer's link button writes, so both kinds of
  // link route the same way.
  const handleLinkPress = useCallback(async (url: string) => {
    // see #1418, was having issue opening youtube urls on android
    // b/c canOpenURL was returning false
    // however, just opening the url without that check works.
    // posted to expo discord in case it's a bug:
    // https://discord.com/channels/695411232856997968/1287818543660011550
    try {
      const parsed = new URL(url);

      if (parsed.hostname.includes('gripp.it') && Platform.OS !== 'web') {
        const path = parsed.pathname + parsed.search + parsed.hash;
        router.push(path);
        return;
      }
    } catch (e) {
      // Only catches URL parsing errors, not Linking errors
    }
    try {
      await Linking.openURL(url);
    } catch (error) {
      // Never rejects: this runs from press handlers (Hyperlink and the
      // formatted-link <Text>), where a rejection would go unhandled.
      console.error('Failed to open link:', error);
    }
  }, []);

  // One flat, KEYLESS list of mention spans and formatted segments, spread
  // variadically into the message <Text> below. react-native-hyperlink
  // destructures every child's props while walking the tree, and on React 19
  // reading `props.key` logs "`key` is not a prop" for any element created with
  // one. Variadic children need no keys, so no element here carries the
  // warning getter.
  const messageBody: ReactNode[] = getMentionTextParts(
    replaceMentionPlaceholderWithNames(
      text,
      message.userMentions,
      t('mentions.unknownUser')
    )
  ).flatMap((part): ReactNode[] =>
    part.isMention
      ? [
          <Text style={styles.mentionHighlight}>
            @{(part as any).name}
          </Text>,
        ]
      : renderFormattedMessageText(part.text, { onLinkPress: handleLinkPress })
  );

  return (
    <View
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        const thumbnailWidth = getImageThumbnailWidth(
          width,
          imageDimensions.width ?? undefined
        );
        setMediaWidth(thumbnailWidth);
      }}
    >
      <View>
        <>
          {text && (
            <Hyperlink
              linkDefault={false}
              linkStyle={{ color: Colors.blue }}
              onPress={handleLinkPress}
            >
              {createElement(
                MessageText,
                {
                  style: [
                    props.textStyle,
                    isOnlyEmojis(text)
                      ? styles.emoji
                      : messageStyles.messageText,
                  ],
                },
                ...messageBody
              )}
            </Hyperlink>
          )}
          {needsTranslation && (
            <View style={styles.translateButton}>
              <Pressable
                onPress={() => {
                  if (text === originalText) {
                    setText(translatedText);
                  } else {
                    setText(originalText);
                  }
                }}
              >
                <Text style={styles.translationButton}>
                  {text === originalText
                    ? t('translation.seeTranslation')
                    : t('translation.seeOriginal')}
                </Text>
              </Pressable>
            </View>
          )}
          <MessageMedia
            media={props.message.media}
            displayWidth={mediaWidth}
            onImagePress={
              Platform.OS !== 'web' ? props.onImagePress : undefined
            }
          />
        </>
      </View>
    </View>
  );
};

/**
 * Paper's `Text` is heavily overloaded, so calling `createElement` on it
 * directly resolves to the intrinsic-element signature and mistypes the
 * children. Narrowing it to the one prop this call passes keeps the variadic
 * form type-safe.
 */
const MessageText = Text as ComponentType<{ style?: StyleProp<TextStyle> }>;

const styles = StyleSheet.create({
  mentionHighlight: {
    color: Colors.primaryDark,
    fontWeight: '400',
    flexWrap: 'wrap',
    flexShrink: 1,
  },
  translateButton: {
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  emoji: {
    fontSize: 48,
  },
  translationButton: {
    color: Colors.primaryDark,
    fontSize: 14,
    lineHeight: 16.8,
    marginTop: 8,
  },
});
